import { useRef, useState, useEffect } from "react";
import api from "../../utils/api";
import { getImageUrl } from "../../utils/imageUtils";

// ─── Theme ───────────────────────────────────────────────────────────────────
const G  = "#15803d";
const G3 = "rgba(21,128,61,0.15)";
const O  = "#F97316";
const O2 = "rgba(249,115,22,0.12)";

// ─── Responsive helper ────────────────────────────────────────────────────────

export function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler); // safari fallback
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, [breakpoint]);
  return isMobile;
}


export const INP = {
  width: "100%", padding: "7px 10px", borderRadius: 7,
  border: "1.5px solid #e5e7eb", fontSize: 13, color: "#111827",
  outline: "none", boxSizing: "border-box", background: "#fff",
};
export const LBL = {
  fontSize: 10, color: "#9ca3af", fontWeight: 600,
  marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.05em",
};


export const THEME = { G, G3, O, O2 };


export const tblHead = (cols, isMobile) => ({
  display: "grid", gridTemplateColumns: cols, columnGap: 16,
  padding: "11px 18px",
  background: "linear-gradient(180deg, #fafbfc 0%, #f3f4f6 100%)",
  borderBottom: "1px solid #e5e7eb",
  fontSize: 10, color: "#6b7280", fontWeight: 700,
  letterSpacing: "0.07em", textTransform: "uppercase",
  minWidth: isMobile ? 600 : undefined,
});

const tblRowBase = (cols, isLast, isMobile) => ({
  display: "grid", gridTemplateColumns: cols, columnGap: 16,
  padding: "12px 18px", alignItems: "center",
  borderBottom: isLast ? "none" : "1px solid #f3f4f6",
  fontSize: 12, color: "#374151", transition: "background 0.12s ease",
  minWidth: isMobile ? 600 : undefined,
});

export function TblRow({ cols, isLast, children }) {
  const [hov, setHov] = useState(false);
  const isMobile = useIsMobile();
  return (
    <div
      style={{ ...tblRowBase(cols, isLast, isMobile), background: hov ? "#f8fafc" : "#fff" }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      {children}
    </div>
  );
}

export function TblCell({ children, fit }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", minWidth: 0, width: "100%", overflow: "hidden",
      justifyContent: fit ? "flex-start" : undefined,
      alignSelf: fit ? "center" : undefined,
    }}>
      {children}
    </div>
  );
}

export function Pill({ children, style }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: "max-content", maxWidth: "100%",
      padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 600,
      whiteSpace: "nowrap", lineHeight: 1.3, ...style,
    }}>
      {children}
    </span>
  );
}

export const actBtn = (kind) => ({
  padding: "5px 11px", borderRadius: 6, fontSize: 10, fontWeight: 600,
  cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.15s",
  ...(kind === "edit"
    ? { background: G3, border: `1px solid ${G}`, color: G }
    : { background: "rgba(239,68,68,0.07)", border: "1px solid #fecaca", color: "#dc2626" }),
});

export const TBL_SELECT = {
  padding: "5px 10px", borderRadius: 7, fontSize: 11, color: "#111827",
  border: "1.5px solid #e5e7eb", outline: "none", background: "#fff",
  width: "max-content", minWidth: 108, maxWidth: 128, cursor: "pointer",
  boxSizing: "border-box",
};

export const newsStatusPill = (status) => ({
  background: status === "published" ? G3 : status === "rejected" ? "rgba(239,68,68,0.10)" : O2,
  color: status === "published" ? G : status === "rejected" ? "#ef4444" : O,
  border: `1px solid ${status === "published" ? G : status === "rejected" ? "#ef4444" : O}`,
});

export const rolePill = (role) => ({
  background: role === "admin" ? G3 : role === "editor" ? O2 : role === "reporter" ? "rgba(99,102,241,0.10)" : "#f3f4f6",
  color: role === "admin" ? G : role === "editor" ? O : role === "reporter" ? "#6366f1" : "#6b7280",
  border: `1px solid ${role === "admin" ? G : role === "editor" ? O : role === "reporter" ? "#6366f1" : "#e5e7eb"}`,
});

export const badgeStyle = (s) => ({
  display: "inline-block", padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 600,
  background: s === "Pending" ? O2 : G3,
  color:      s === "Pending" ? O  : G,
  border:     `1px solid ${s === "Pending" ? O : G}`,
});

export const rowStyle = (last, isMobile) => ({
  display: "grid", gridTemplateColumns: "1.5fr 2fr 1.2fr 1fr 80px",
  padding: "10px 16px", alignItems: "center",
  borderBottom: last ? "none" : "1px solid #f3f4f6",
  fontSize: 12, color: "#374151",
  minWidth: isMobile ? 600 : undefined,
});

// ─── Shared Styles ───────────────────────────────────────────────────────────
export const S = {
  root:      { display: "flex", height: "100vh", width: "100%", fontFamily: "'Segoe UI', system-ui, sans-serif", background: "#f3f4f6", overflow: "hidden" },
  sidebar:   { width: 260, minWidth: 260, background: G, display: "flex", flexDirection: "column", height: "100vh", overflowY: "auto", overflowX: "hidden", flexShrink: 0 },
  logo:      { padding: "16px 14px 14px", borderBottom: "1px solid rgba(255,255,255,0.12)" },
  navSection:{ flex: 1, padding: "8px 0" },
  navIcon:   { fontSize: 18, color: "rgba(255,255,255,0.75)", width: 20, textAlign: "center" },
  exitBtn:   { padding: "14px 16px", borderTop: "1px solid rgba(255,255,255,0.12)", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", color: "rgba(255,255,255,0.6)", fontSize: 13 },
  main:      { flex: 1, display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" },
  topbar:    { height: 56, background: "#fff", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 24px", flexShrink: 0, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
  topTitle:  { fontSize: 18, fontWeight: 700, color: "#111827" },
  topRight:  { display: "flex", alignItems: "center", gap: 14 },
  bellWrap:  { position: "relative", cursor: "pointer" },
  badge:     { position: "absolute", top: -4, right: -6, background: O, color: "#fff", borderRadius: "50%", width: 16, height: 16, fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 },
  avatar:    { width: 32, height: 32, borderRadius: "50%", background: G, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, cursor: "pointer", border: `2px solid ${G}`, transition: "box-shadow 0.2s" },
  content:   { flex: 1, overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 16, background: "#f8fafc" },
  // ── statsGrid ra midRow aba FUNCTION huna, isMobile pass garnu parcha ──
  statsGrid: (isMobile) => ({ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)", gap: 12 }),
  statIcon:  { width: 42, height: 42, borderRadius: 8, background: O2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 },
  statCard:  { background: "#fff", borderRadius: 12, padding: "14px 16px", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 12, boxShadow: "0 1px 2px rgba(0,0,0,0.04)", transition: "box-shadow 0.2s, transform 0.2s" },
  statVal:   { fontSize: 20, fontWeight: 800, color: "#111827", lineHeight: 1 },
  statLabel: { fontSize: 11, color: "#6b7280", marginTop: 2 },
  statDelta: { fontSize: 10, color: G, fontWeight: 600, marginTop: 3 },
  midRow:    (isMobile) => ({ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 280px", gap: 12 }),
  card:      { background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", padding: 16, boxShadow: "0 1px 2px rgba(0,0,0,0.03)" },
  cardTitle: { fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 14 },
  chart:     { display: "flex", alignItems: "flex-end", gap: 8, height: 100 },
  barWrap:   { display: "flex", flexDirection: "column", alignItems: "center", flex: 1, gap: 4 },
  barLabel:  { fontSize: 9, color: "#9ca3af" },
  quickGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  qBtn:      { padding: "10px 8px", borderRadius: 8, border: `1px solid ${G}`, background: G3, color: G, fontSize: 11, fontWeight: 600, cursor: "pointer", textAlign: "center" },
  // ── tableWrap ma horizontal scroll add gareko, so mobile ma columns cut nahos, swipe garna milos ──
  tableWrap: { background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
  tableScroll: { overflowX: "auto", WebkitOverflowScrolling: "touch" },
  colHeader: (isMobile) => ({ display: "grid", gridTemplateColumns: "1.5fr 2fr 1.2fr 1fr 80px", padding: "8px 16px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: 10, color: "#9ca3af", fontWeight: 700, letterSpacing: "0.06em", minWidth: isMobile ? 600 : undefined }),
};

// ─── Shared Status Badge ──────────────────────────────────────────────────────
export function StatusBadge({ status, language = "ne" }) {
  const map = {
    pending:  { bg: O2,                          color: O,         border: O         },
    approved: { bg: G3,                          color: G,         border: G         },
    accepted: { bg: G3,                          color: G,         border: G         },
    rejected: { bg: "rgba(239,68,68,0.10)",      color: "#ef4444", border: "#ef4444" },
  };
  const c = map[status?.toLowerCase()] || map.pending;
  const labelMap = {
    ne: {
      pending:  "विचाराधीन",
      approved: "स्वीकृत",
      accepted: "स्वीकृत",
      rejected: "अस्वीकृत",
    },
    en: {
      pending:  "Pending",
      approved: "Approved",
      accepted: "Approved",
      rejected: "Rejected",
    },
  };
  const labels = labelMap[language] || labelMap.ne;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: "max-content", padding: "3px 10px", borderRadius: 20,
      fontSize: 10, fontWeight: 600, whiteSpace: "nowrap", lineHeight: 1.3,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
    }}>
      {labels[status?.toLowerCase()] || status}
    </span>
  );
}

// ─── Image Upload Field ───────────────────────────────────────────────────────
export function ImageUploadField({ value, onChange }) {
  const [error, setError]       = useState("");
  const [dragging, setDragging] = useState(false);
  const fileRef                 = useRef();

  function handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("कृपया PNG, JPG वा WEBP मात्र चुन्नुहोस्।");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("फाइल 5MB भन्दा सानो हुनुपर्छ।");
      return;
    }
    setError("");
    const preview = URL.createObjectURL(file);
    onChange({ file, preview, url: preview });
  }

  function handleDrop(e) {
    e.preventDefault(); setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  }

  const previewSrc = typeof value === "object" && value
    ? (value.preview || (value.file ? URL.createObjectURL(value.file) : ""))
    : getImageUrl(value);

  const hiddenInput = (
    <input
      ref={fileRef}
      type="file"
      accept="image/png,image/jpeg,image/webp"
      style={{ display: "none" }}
      onChange={e => { handleFile(e.target.files[0]); e.target.value = ""; }}
    />
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {hiddenInput}

      {!previewSrc && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current.click()}
          style={{
            border: `2px dashed ${dragging ? G : "#d1d5db"}`,
            borderRadius: 10, padding: "22px 16px",
            textAlign: "center",
            cursor: "pointer",
            background: dragging ? G3 : "#fafafa",
            transition: "all 0.15s",
          }}
        >
          <div style={{ fontSize: 28 }}>🖼️</div>
          <div style={{ fontSize: 12, color: "#374151", marginTop: 6 }}>
            यहाँ <strong>drag & drop</strong> गर्नुहोस् वा{" "}
            <span style={{ color: G, fontWeight: 700, textDecoration: "underline" }}>
              device बाट छान्नुहोस्
            </span>
          </div>
          <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 4 }}>
            PNG · JPG · WEBP · max 5MB
          </div>
        </div>
      )}

      {error && (
        <div style={{ fontSize: 11, color: "#ef4444", background: "rgba(239,68,68,0.07)", border: "1px solid #fca5a5", borderRadius: 6, padding: "6px 10px" }}>
          ⚠ {error}
        </div>
      )}

      {previewSrc && (
        <div style={{ position: "relative" }}>
          <img
            src={previewSrc} alt="preview"
            style={{ width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: 8, border: `1.5px solid ${G}`, display: "block" }}
            onError={e => {
              if (!previewSrc.startsWith("blob:")) {
                e.target.style.display = "none";
                setError("छवि लोड हुन सकेन।");
              }
            }}
          />
          <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 6 }}>
            <button
              onClick={() => fileRef.current.click()}
              style={{ background: "rgba(255,255,255,0.92)", border: `1px solid ${G}`, color: G, borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              ✏️ बदल्नुहोस्
            </button>
            <button
              onClick={() => { onChange(""); setError(""); }}
              style={{ background: "rgba(255,255,255,0.92)", border: "1px solid #ef4444", color: "#ef4444", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              🗑 हटाउनुहोस्
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Gallery Upload Button ────────────────────────────────────────────────────
// Supports selecting MULTIPLE photos at once (both via file picker and drag/drop-style multi-select).
export function GalleryUploadBtn({ onUpload }) {
  const fileRef = useRef();

  function handleFiles(fileList) {
    const files = Array.from(fileList || []);
    files.forEach((file) => {
      if (!file || !file.type.startsWith("image/")) return;
      if (file.size > 5 * 1024 * 1024) { alert(`"${file.name}" 5MB भन्दा ठूलो भएकोले छोडियो।`); return; }
      const preview = URL.createObjectURL(file);
      onUpload({ file, preview, url: preview });
    });
  }

  return (
    <>
      <button
        onClick={() => fileRef.current.click()}
        style={{
          background: G3, border: `1px solid ${G}`, color: G,
          borderRadius: 6, padding: "4px 12px", fontSize: 11,
          fontWeight: 600, cursor: "pointer",
        }}>
        + फोटो थप्नुहोस्
      </button>
      <input
        ref={fileRef} type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        style={{ display: "none" }}
        onChange={e => { handleFiles(e.target.files); e.target.value = ""; }}
      />
    </>
  );
}

// ─── Sector Row (used inside SectorsPage table) ───────────────────────────────
export function SectorRow({ s, idx, tags, isLast, onEdit, onDelete }) {
  const [hov, setHov] = useState(false);
  const isMobile = useIsMobile();
  const VISIBLE_TAGS  = 6;
  const shown         = tags.slice(0, VISIBLE_TAGS);
  const extra         = tags.length - VISIBLE_TAGS;
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display:"grid", gridTemplateColumns:"52px 1.8fr 3fr 90px 110px",
        padding:"13px 20px", alignItems:"center",
        borderBottom: isLast ? "none" : "1px solid #f3f4f6",
        background: hov ? "#f8fafc" : "#fff",
        transition:"background 0.12s",
        minWidth: isMobile ? 600 : undefined,
      }}
    >
      <div style={{ display:"flex", alignItems:"center" }}>
        <div style={{
          width:30, height:30, borderRadius:"50%",
          background: hov ? O2 : "#f3f4f6",
          color: hov ? O : "#9ca3af",
          fontSize:12, fontWeight:700,
          display:"flex", alignItems:"center", justifyContent:"center",
          border: hov ? `1.5px solid ${O}` : "1.5px solid #e5e7eb",
          transition:"all 0.15s", flexShrink:0,
        }}>
          {s.order || idx + 1}
        </div>
      </div>

      <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0 }}>
        <div style={{
          width:36, height:36, borderRadius:8, background:G3,
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:18, flexShrink:0,
        }}>
          {s.icon || "📋"}
        </div>
        <div style={{ minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#111827", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {s.name}
          </div>
          <div style={{ fontSize:10, color:"#9ca3af", marginTop:1 }}>{tags.length} उपक्षेत्र</div>
        </div>
      </div>

      <div style={{ display:"flex", flexWrap:"wrap", gap:5, alignItems:"center" }}>
        {shown.map(t => (
          <span key={t} style={{
            display:"inline-block", padding:"2px 9px", borderRadius:20,
            fontSize:10, fontWeight:600, background:G3, color:G,
            border:`1px solid ${G}`, whiteSpace:"nowrap",
          }}>{t}</span>
        ))}
        {extra > 0 && (
          <span style={{
            display:"inline-block", padding:"2px 9px", borderRadius:20,
            fontSize:10, fontWeight:600, background:"#f3f4f6", color:"#6b7280",
            border:"1px solid #e5e7eb", whiteSpace:"nowrap",
          }}>+{extra} थप</span>
        )}
        {tags.length === 0 && (
          <span style={{ fontSize:11, color:"#d1d5db", fontStyle:"italic" }}>उपक्षेत्र छैन</span>
        )}
      </div>

      <div style={{ textAlign:"center" }}>
        <span style={{
          display:"inline-block", padding:"3px 10px", borderRadius:20,
          fontSize:11, fontWeight:700,
          background: tags.length > 0 ? O2 : "#f3f4f6",
          color:      tags.length > 0 ? O  : "#9ca3af",
          border:`1px solid ${tags.length > 0 ? O : "#e5e7eb"}`,
        }}>{tags.length}</span>
      </div>

      <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
        <button onClick={onEdit} style={{
          background:G3, border:`1px solid ${G}`, color:G,
          borderRadius:6, padding:"5px 12px", fontSize:11,
          cursor:"pointer", fontWeight:600, whiteSpace:"nowrap",
        }}>✏️ सम्पादन</button>
        <button onClick={onDelete} style={{
          background:"rgba(239,68,68,0.08)", border:"1px solid #fca5a5", color:"#ef4444",
          borderRadius:6, padding:"5px 10px", fontSize:11,
          cursor:"pointer", fontWeight:600, whiteSpace:"nowrap",
        }}>🗑</button>
      </div>
    </div>
  );
}
